import type { ComponentProps, ReactNode } from 'react';
import { Fragment } from 'react';

import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Text from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ActionCardButton = Omit<
  ComponentProps<typeof Button>,
  'children'
> & {
  label: string;
  icon?: LucideIcon;
  to?: string;
};

export type ActionCardItemAction = Omit<
  ComponentProps<typeof Button>,
  'children'
> & {
  label: string;
  icon?: LucideIcon;
  to?: string;
};

export type ActionCardItem = {
  id: string;
  icon?: LucideIcon;
  label: string;
  description?: string;
  action?: ActionCardItemAction;
};

export type ActionCardProps = {
  icon?: LucideIcon;
  title: string;
  description?: string;
  children?: ReactNode;
  primaryAction?: ActionCardButton;
  secondaryAction?: ActionCardButton;
  headerAction?: ActionCardButton;
  align?: 'start' | 'center';
  bodyPadding?: string;
  items?: ActionCardItem[];
  showItemDividers?: boolean;
  itemSpacing?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  statusContent?: ReactNode;
  footerContent?: ReactNode;
  'data-testid'?: string;
};

function renderItems(
  items?: ActionCardItem[],
  options?: {
    showDividers?: boolean;
    spacing?: string;
  }
) {
  if (!items?.length) return null;

  const { showDividers = false } = options ?? {};

  return (
    <div className="flex w-full flex-col gap-5 sm:gap-4">
      {items.map((item, index) => (
        <Fragment key={item.id}>
          <div className="flex w-full flex-col gap-2 sm:gap-1">
            <div className="flex w-full flex-row items-center gap-3 sm:gap-2">
              <div className="flex flex-1 flex-row items-center gap-3 sm:gap-2">
                {item.icon &&
                  (() => {
                    const ItemIcon = item.icon;
                    return <ItemIcon size={24} className="min-w-6" />;
                  })()}
                <Text weight="medium">{item.label}</Text>
              </div>

              {item.action &&
                (() => {
                  const {
                    label,
                    icon: ActionIcon,
                    to,
                    ...actionProps
                  } = item.action;
                  const content = (
                    <>
                      {label}
                      {ActionIcon && <ActionIcon data-icon="inline-end" />}
                    </>
                  );
                  if (to) {
                    return (
                      <Button {...actionProps} asChild>
                        <Link to={to}>{content}</Link>
                      </Button>
                    );
                  }
                  return <Button {...actionProps}>{content}</Button>;
                })()}
            </div>

            {item.description && (
              <Text size="sm" variant="muted">
                {item.description}
              </Text>
            )}
          </div>

          {showDividers && index < items.length - 1 && <Separator />}
        </Fragment>
      ))}
    </div>
  );
}

function renderAction(
  action?: ActionCardButton,
  fallbackVariant: ComponentProps<typeof Button>['variant'] = 'default'
) {
  if (!action) return null;

  const { label, icon: ActionIcon, variant, to, ...rest } = action;
  const content = (
    <>
      {ActionIcon && <ActionIcon data-icon="inline-start" />}
      {label}
    </>
  );

  if (to) {
    return (
      <Button {...rest} variant={variant ?? fallbackVariant} asChild>
        <Link to={to}>{content}</Link>
      </Button>
    );
  }

  return (
    <Button {...rest} variant={variant ?? fallbackVariant}>
      {content}
    </Button>
  );
}

const bodyPaddingClass: Record<string, string> = {
  none: 'px-0',
  sm: 'px-4',
  lg: 'px-8',
};

export function ActionCard({
  icon: IconComp,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  headerAction,
  bodyPadding = 'sm',
  items,
  showItemDividers = true,
  itemSpacing = 'sm',
  statusContent,
  footerContent,
  'data-testid': dataTestId,
}: ActionCardProps) {
  const hasItems = Boolean(items?.length);
  const hasChildren = Boolean(children);
  const hasStatus = Boolean(statusContent);
  const hasFooter = Boolean(footerContent);

  return (
    <div className="flex w-full flex-col gap-2 sm:gap-1.5">
      {headerAction && (
        <div className="flex justify-end">
          {renderAction(headerAction, 'default')}
        </div>
      )}
      <Card className="w-full" data-testid={dataTestId}>
        <CardHeader className="px-4">
          <div className="flex flex-col gap-3 sm:gap-2">
            <div className="flex flex-row items-center gap-3 sm:gap-2">
              {IconComp && <IconComp size={24} />}
              <Text size="lg" weight="semibold">
                {title}
              </Text>
            </div>
            {description && (
              <Text variant="muted" size="sm">
                {description}
              </Text>
            )}
          </div>
        </CardHeader>
        <CardContent className={cn(bodyPaddingClass[bodyPadding])}>
          <div className="flex flex-col gap-5 sm:gap-4">
            {hasStatus && (
              <div className="flex w-full flex-col gap-5 sm:gap-4">
                {statusContent}
              </div>
            )}

            {hasItems &&
              renderItems(items, {
                showDividers: showItemDividers,
                spacing: itemSpacing,
              })}

            {hasChildren && (
              <div className="flex w-full flex-col gap-3 sm:gap-2">
                {children}
              </div>
            )}

            {hasFooter && (
              <div className="flex w-full flex-col gap-5 sm:gap-4">
                {footerContent}
              </div>
            )}

            {(primaryAction ?? secondaryAction) && (
              <div className="flex w-full flex-row flex-wrap justify-center gap-3 sm:gap-2">
                {renderAction(primaryAction)}
                {renderAction(secondaryAction, 'neutral')}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
